import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { ThirdPartyTypeEnum, getDocumentName } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { ThirdPartiesCmp_SomeOrganizations_QueryDocument } from '../../../../../components/ThirdParties/queries/some-organizations.query';
import { ThirdPartiesCmp_SomePeople_QueryDocument } from '../../../../../components/ThirdParties/queries/some-people.query';
import { OrganizationPage_OneOrganization_QueryDocument } from '../../../../../pages/Organization/hooks/useFindOneOrganizationQuery.hook';
import { PersonPage_OnePerson_QueryDocument } from '../../../../../pages/Person/hooks/useFindOnePersonQuery.hook';

import { AddressesActionKind } from '../enums/action-kind.enum';
import type { AddressesState } from '../types/state.type';
import type { AddressesAction } from '../types/actions.type';
import type { AddressPaths } from '../types/paths.type';
import type { ThirdPartyInfo } from '../types/third-party-info.type';

function addressesReducer(
  state: Readonly<AddressesState>,
  action: AddressesAction,
): AddressesState {
  const actionType = action.type;

  switch (actionType) {
    case AddressesActionKind.ClearState:
      return (
        action.state ?? {
          idToRemove: null,
          isRemoveDialogOpen: false,
          idToUpdate: null,
          isUpdateDrawerOpen: false,
          isCreateDrawerOpen: false,
        }
      );
    case AddressesActionKind.StartCreate:
      return {
        ...state,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: true,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case AddressesActionKind.StartRemove:
      return {
        ...state,
        idToRemove: action.payload,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isRemoveDialogOpen: true,
        isUpdateDrawerOpen: false,
      };
    case AddressesActionKind.StartUpdate:
      return {
        ...state,
        idToRemove: null,
        idToUpdate: action.payload,
        isCreateDrawerOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: true,
      };
    case AddressesActionKind.EndCreate:
      return {
        ...state,
        isCreateDrawerOpen: false,
      };
    case AddressesActionKind.EndRemove:
      return {
        ...state,
        idToRemove: null,
        isRemoveDialogOpen: false,
      };
    case AddressesActionKind.EndUpdate:
      return {
        ...state,
        idToUpdate: null,
        isUpdateDrawerOpen: false,
      };
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unknown action type: ${actionType}`);
  }
}

interface Params {
  readonly paths?: AddressPaths;
  readonly thirdPartyInfo: ThirdPartyInfo;
}

export const useAddressesReducer = ({
  paths,
  thirdPartyInfo,
}: Readonly<Params>) => {
  const { pathname: currentPath } = useLocation();
  const { addressId } = useParams();
  const queryClient = useQueryClient();

  const resolvedState = useMemo(
    () => ({
      idToRemove:
        currentPath === paths?.editDelete && addressId
          ? (addressId as Id)
          : null,
      idToUpdate:
        currentPath === paths?.editModify && addressId
          ? (addressId as Id)
          : null,
      isRemoveDialogOpen: currentPath === paths?.editDelete,
      isUpdateDrawerOpen: currentPath === paths?.editModify,
      isCreateDrawerOpen: currentPath === paths?.editNew,
    }),
    [
      addressId,
      paths?.editDelete,
      paths?.editModify,
      paths?.editNew,
      currentPath,
    ],
  );

  const [state, dispatch] = useReducer(addressesReducer, resolvedState);

  useEffect(() => {
    dispatch({
      type: AddressesActionKind.ClearState,
      state: resolvedState,
    });
  }, [resolvedState]);

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const invalidateSome = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [
        getDocumentName(
          thirdPartyInfo.kind === ThirdPartyTypeEnum.Organization
            ? ThirdPartiesCmp_SomeOrganizations_QueryDocument
            : ThirdPartiesCmp_SomePeople_QueryDocument,
        ),
        {
          statusId: thirdPartyInfo.statusId,
          isInternal: thirdPartyInfo.isInternal,
        },
      ],
    });
  }, [
    queryClient,
    thirdPartyInfo.isInternal,
    thirdPartyInfo.kind,
    thirdPartyInfo.statusId,
  ]);

  const invalidateOne = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [
        getDocumentName(
          thirdPartyInfo.kind === ThirdPartyTypeEnum.Organization
            ? OrganizationPage_OneOrganization_QueryDocument
            : PersonPage_OnePerson_QueryDocument,
        ),
        {
          id: thirdPartyInfo._id,
        },
      ],
    });
  }, [queryClient, thirdPartyInfo._id, thirdPartyInfo.kind]);

  const handleClearState = useCallback(() => {
    paths
      ? void navigate(paths.edit)
      : dispatch({ type: AddressesActionKind.ClearState });
  }, [paths, navigate]);

  const handleStartCreate = useCallback(() => {
    paths
      ? void navigate(paths.editNew)
      : dispatch({ type: AddressesActionKind.StartCreate });
  }, [navigate, paths]);

  const handleStartRemove = useCallback(
    (id: Id) => {
      paths
        ? void navigate(buildPath(paths.editDelete, { addressId: id }))
        : dispatch({
            type: AddressesActionKind.StartRemove,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleStartUpdate = useCallback(
    (id: Id) => {
      paths
        ? void navigate(buildPath(paths.editModify, { addressId: id }))
        : dispatch({
            type: AddressesActionKind.StartUpdate,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleCreateSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateSome();
          invalidateOne();
          enqueueSnackbar("L'adresse a bien été créée.");
          void navigate(paths.edit);
        })()
      : dispatch({ type: AddressesActionKind.EndCreate });
  }, [enqueueSnackbar, navigate, paths, invalidateSome, invalidateOne]);

  const handleUpdateSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateSome();
          invalidateOne();
          enqueueSnackbar("L'adresse a bien été mise à jour.");
          void navigate(paths.edit);
        })()
      : dispatch({ type: AddressesActionKind.EndUpdate });
  }, [enqueueSnackbar, navigate, paths, invalidateSome, invalidateOne]);

  const handleRemoveSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateSome();
          invalidateOne();
          enqueueSnackbar("L'adresse a bien été supprimée.");
          void navigate(paths.edit);
        })()
      : dispatch({ type: AddressesActionKind.EndRemove });
  }, [enqueueSnackbar, navigate, paths, invalidateSome, invalidateOne]);

  return {
    state,
    dispatch,
    handleClearState,
    handleStartCreate,
    handleStartRemove,
    handleStartUpdate,
    handleCreateSuccess,
    handleRemoveSuccess,
    handleUpdateSuccess,
  };
};
